import { useRef, useSyncExternalStore } from 'react';

/**
 * Hook that returns the current date and updates it at specified interval
 *
 * Uses `useSyncExternalStore` to subscribe to a time interval and automatically
 * re-render the component when the date updates. The date is stored in a ref
 * to maintain referential stability between updates.
 *
 * @param updateInterval - Update interval in milliseconds (default: 60000 - 1 minute)
 *
 * @returns Current date object that updates at specified interval
 *
 * @example
 * ```tsx
 * import { formatRelativeDateTime } from '@/shared/date-fns'
 * import { useCurrentDate } from '@/shared/lib/hooks'
 * import { uk } from 'date-fns/locale'
 *
 * function AppointmentTime({ appointmentDate }) {
 *   // Update every minute (default)
 *   const now = useCurrentDate()
 *
 *   return (
 *     <div>{formatRelativeDateTime(appointmentDate, { locale: uk, referenceDate: now })}</div>
 *   )
 * }
 *
 * // Update every 10 seconds for more precise timing
 * const now = useCurrentDate(10000)
 *
 * // Update every 5 minutes to save resources
 * const now = useCurrentDate(5 * 60 * 1000)
 * ```
 */
export function useCurrentDate(updateInterval: number = 60000): Date {
  const currentDateRef = useRef(new Date());

  return useSyncExternalStore(
    (callback: () => void) => {
      const intervalId = setInterval(() => {
        currentDateRef.current = new Date();
        callback();
      }, updateInterval);

      return () => clearInterval(intervalId);
    },
    () => currentDateRef.current,
  );
}
